import * as React from 'react';
import { SxProps, Theme, Toolbar as MatToolbar } from '@mui/material';
import { createButton, ButtonBlueprint } from '../../factories';
import { keygen } from '../../utils';

const styles: Record<string, SxProps<Theme>> = {
  toolbarRoot: {
    '&[class*="MuiToolbar"]': {
      minHeight: 0
    },
    // remove padding from main toolbar container
    '&[class*="MuiToolbar-gutters"]': {
      paddingLeft: 0,
      paddingRight: 0
    },
    // remove double margin from adjacent buttons
    '&>.toolbar-actionGroup-left>button:not(:first-child)': {
      marginLeft: 0
    },
    '&>.toolbar-actionGroup-right>button:not(:first-child)': {
      marginLeft: 0
    },
    // remove margin from left and right toolbar groups
    '&>.toolbar-actionGroup-left>button:first-child': {
      marginLeft: 0
    },
    '&>.toolbar-actionGroup-right>button:last-child': {
      marginRight: 0
    }
  }
};

type Position = 'left' | 'right';
type ToolbarVariant = 'regular' | 'dense';

interface ToolbarActionGroup {
  // TODO: this should be required to ensure that the group isn't recreated every re-render
  id?: string | number; // use this for specs
  items: ButtonBlueprint[];
  position: Position;
}

export interface ToolbarProps {
  items: ToolbarActionGroup[];
  variant?: ToolbarVariant;
  classes?: any;
  sx?: SxProps<Theme>;
}

const buildLeftGroup = (items: ToolbarActionGroup[]) => {
  const leftItems = items.filter(child => child.position === 'left');
  if (leftItems.length === 0) {
    return [];
  }

  return leftItems.map((group: ToolbarActionGroup) => {
    const key = group.id ? group.id : keygen();
    /* istanbul ignore next */
    if (process.env.NODE_ENV !== 'production') {
      checkInvalidGroupId(group.id);
    }
    return (
      <div key={key} style={{ flexGrow: 1, display: 'flex' }} className="toolbar-actionGroup-left">
        {group.items.map((item, index) => {
          /* istanbul ignore next */
          if (process.env.NODE_ENV !== 'production') {
            checkInvalidButtonId(item.id);
          }
          return createButton({ id: index, ...item });
        })}
      </div>
    );
  });
};

const buildRightGroup = (items: ToolbarActionGroup[]) => {
  const rightItems = items.filter(child => child.position === 'right');
  if (rightItems.length === 0) {
    return [];
  }

  return rightItems.map((group: ToolbarActionGroup) => {
    const key = group.id ? group.id : keygen();
    /* istanbul ignore next */
    if (process.env.NODE_ENV !== 'production') {
      checkInvalidGroupId(group.id);
    }
    return (
      <React.Fragment key={key}>
        <div style={{ flexGrow: 1, display: 'flex' }} key={keygen()} />
        <div key={key} className="toolbar-actionGroup-right">
          {group.items.map((item, index) => {
            /* istanbul ignore next */
            if (process.env.NODE_ENV !== 'production') {
              checkInvalidButtonId(item.id);
            }
            return createButton({ id: index, ...item });
          })}
        </div>
      </React.Fragment>
    );
  });
};

const Toolbar: React.FunctionComponent<ToolbarProps> = ({ items, sx, ...rest }) => {
  const { toolbarRoot } = styles;
  return (
    <MatToolbar sx={[toolbarRoot, sx] as SxProps<Theme>} {...rest}>
      {buildLeftGroup(items)}
      {buildRightGroup(items)}
    </MatToolbar>
  );
};

Toolbar.displayName = 'Toolbar';

/* Utilities */
let hasCheckInvalidGroupIdRun = false;
/* istanbul ignore next */
function checkInvalidGroupId(id: string | number | undefined) {
  if (id === undefined && !hasCheckInvalidGroupIdRun) {
    console.warn(
      `Please make sure to pass an id to the toolbar group, otherwise the group will unmount / remount on every re-render instead of updating in place.`
    );
    hasCheckInvalidGroupIdRun = true;
  }
}

let hasCheckInvalidButtonIdRun = false;
/* istanbul ignore next */
function checkInvalidButtonId(id: string | number | undefined) {
  if (id === undefined && !hasCheckInvalidButtonIdRun) {
    console.warn(
      `If the order of the buttons change, please make sure to pass an id to each button, otherwise stale data may be passed to the button.`
    );
    hasCheckInvalidButtonIdRun = true;
  }
}

export { Toolbar };
